import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    defaultOperationDate,
    FormDateField,
    FormNumberField,
    FormSelect,
    FormTextarea,
    FormTextField,
    toOperationTimestamp,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import {
    FISH_GROUP_MANUAL_EVENT_TYPES,
    type FishGroupManualEventType,
    type RecordFishGroupEventInput,
    useRecordFishGroupEvent,
} from '@/domain/fishGroups';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
    fishGroupName: string;
    currentLocation?: { cageName: string; locationName: string; fromDate: string } | null;
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    eventType: FishGroupManualEventType | '';
    eventDate: Date | null;
    fishCountBefore: string;
    fishCountAfter: string;
    notes: string;
    harvestedCount: string;
    harvestedWeight: string;
    delousingType: string;
    delousingDose: string;
    delousingSubstance: string;
}

// A factory, not a constant: the date defaults to today, and a module-level
// constant would freeze "today" for the lifetime of the tab.
function makeDefaultValues(): FormValues {
    return {
        eventType: '',
        eventDate: defaultOperationDate(),
        fishCountBefore: '',
        fishCountAfter: '',
        notes: '',
        harvestedCount: '',
        harvestedWeight: '',
        delousingType: '',
        delousingDose: '',
        delousingSubstance: '',
    };
}

const TYPES_WITH_COUNTS: FishGroupManualEventType[] = ['harvest'];

export default function RecordFishGroupEventModal(props: Props) {
    const { clientId, fishGroupId, fishGroupName, currentLocation, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        watch,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: makeDefaultValues() });

    const eventType = watch('eventType');
    const showCounts = TYPES_WITH_COUNTS.includes(eventType as FishGroupManualEventType);

    const handleClose = () => {
        if (isSubmitting) return;
        reset(makeDefaultValues());
        onClose();
    };

    const {
        recordEvent,
        error: serverError,
        reset: resetMutation,
    } = useRecordFishGroupEvent(clientId, fishGroupId, {
        onSuccess: () => {
            ToastSuccess(t('actions.recordEvent.successToast'), 'fish-group-record-event-success');
            reset(makeDefaultValues());
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    // `recordEvent` has no strictly-after guard server-side, but the picker's
    // day-granular `minDate` still lets the operator land on the location's
    // opening day — so pass it anyway to keep events from being timestamped
    // before the location they belong to existed.
    const locationFromDate = currentLocation ? new Date(currentLocation.fromDate) : null;

    // Only the two offered types have a payload shape. The API still accepts
    // mortality / measurement / feeding / treatment for historical rows, but the
    // UI no longer produces them, so their form blocks were removed rather than
    // left unreachable.
    const buildEventData = (values: FormValues): Record<string, unknown> | undefined => {
        switch (values.eventType) {
            case 'harvest':
                return {
                    ...(values.harvestedCount !== ''
                        ? { harvestedCount: Number(values.harvestedCount) }
                        : {}),
                    ...(values.harvestedWeight !== ''
                        ? { harvestedWeight: Number(values.harvestedWeight) }
                        : {}),
                };
            case 'delousing':
                return {
                    ...(values.delousingType.trim() ? { type: values.delousingType.trim() } : {}),
                    ...(values.delousingDose.trim() ? { dose: values.delousingDose.trim() } : {}),
                    ...(values.delousingSubstance.trim()
                        ? { substance: values.delousingSubstance.trim() }
                        : {}),
                };
            default:
                return undefined;
        }
    };

    const onSubmit = async (values: FormValues) => {
        if (!values.eventType || !values.eventDate) return;
        const eventData = buildEventData(values);
        const requestBody: RecordFishGroupEventInput = {
            eventType: values.eventType,
            eventDate: toOperationTimestamp(values.eventDate, { notBefore: locationFromDate }),
            ...(values.fishCountBefore !== ''
                ? { fishCountBefore: Number(values.fishCountBefore) }
                : {}),
            ...(values.fishCountAfter !== ''
                ? { fishCountAfter: Number(values.fishCountAfter) }
                : {}),
            ...(eventData && Object.keys(eventData).length > 0 ? { eventData } : {}),
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        await recordEvent(requestBody).catch(() => {});
    };

    const eventTypeOptions = FISH_GROUP_MANUAL_EVENT_TYPES.map((type) => ({
        value: type,
        label: t(`eventTypes.${type}`),
    }));

    const body = (
        <>
            <p className={styles.contextLine}>
                {t('actions.recordEvent.context')}{' '}
                <span className={styles.contextStrong}>{fishGroupName}</span>
                {currentLocation && (
                    <span className={styles.contextMeta}>
                        {' · '}
                        {currentLocation.cageName} ({currentLocation.locationName})
                    </span>
                )}
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormSelect
                    control={control}
                    name="eventType"
                    label={t('actions.recordEvent.fields.eventType')}
                    placeholder={t('actions.recordEvent.fields.eventTypePlaceholder')}
                    options={eventTypeOptions}
                    rules={{ required: true }}
                />
                <FormDateField
                    control={control}
                    name="eventDate"
                    label={t('actions.fields.eventDate')}
                    rules={{ required: true }}
                    blockFutureDates
                    minDate={locationFromDate}
                />

                {showCounts && (
                    <>
                        <FormNumberField
                            control={control}
                            name="fishCountBefore"
                            label={t('actions.recordEvent.fields.fishCountBefore')}
                            optional
                            min={0}
                        />
                        <FormNumberField
                            control={control}
                            name="fishCountAfter"
                            label={t('actions.recordEvent.fields.fishCountAfter')}
                            optional
                            min={0}
                        />
                    </>
                )}

                {eventType === 'harvest' && (
                    <>
                        <FormNumberField
                            control={control}
                            name="harvestedCount"
                            label={t('actions.recordEvent.fields.harvestedCount')}
                            min={0}
                        />
                        <FormNumberField
                            control={control}
                            name="harvestedWeight"
                            label={t('actions.recordEvent.fields.harvestedWeight')}
                            optional
                            step="0.01"
                            min={0}
                        />
                    </>
                )}

                {eventType === 'delousing' && (
                    <>
                        <FormTextField
                            control={control}
                            name="delousingType"
                            label={t('actions.recordEvent.fields.delousingType')}
                        />
                        <FormTextField
                            control={control}
                            name="delousingSubstance"
                            label={t('actions.recordEvent.fields.delousingSubstance')}
                        />
                        <FormTextField
                            control={control}
                            name="delousingDose"
                            label={t('actions.recordEvent.fields.delousingDose')}
                            optional
                        />
                    </>
                )}

                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" form={formId} disabled={isSubmitting}>
                {t('actions.recordEvent.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.recordEvent.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
